import React, { useState, useEffect } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './StaffScheduling.css';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Deterministic color per coach id (for avatars + coverage blocks)
const COACH_COLORS = ['#667eea', '#4ECDC4', '#FF6B6B', '#F7A440', '#9D84B7', '#27AE60', '#E67E22', '#2a9d8f'];
const colorFor = (id) => COACH_COLORS[Math.abs(Number(id) || 0) % COACH_COLORS.length];
const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';

// Hours between two HH:MM[:SS] strings
const hoursBetween = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = String(start).split(':').map(Number);
  const [eh, em] = String(end).split(':').map(Number);
  return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
};

/**
 * STAFF SCHEDULING — Admin coverage, time-off, and hours
 */
function StaffScheduling({ embedded = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const [staff, setStaff] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [selectedView, setSelectedView] = useState('workhours');

  const mondayStr = (d = new Date()) => {
    const u = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    u.setUTCDate(u.getUTCDate() - ((u.getUTCDay() + 6) % 7));
    return u.toISOString().slice(0, 10);
  };
  const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

  // Coverage (work hours) state
  const [weekStart, setWeekStart] = useState(mondayStr());
  const [weekShifts, setWeekShifts] = useState([]);
  const [savingBlock, setSavingBlock] = useState(false);
  const emptyBlock = {
    coach_ids: [], start_time: '08:00', end_time: '16:00',
    mode: 'single', date: mondayStr(), weekday: new Date().getDay(), occurrences: 12
  };
  const [blockForm, setBlockForm] = useState(emptyBlock);

  // Hours summary (live, from clock entries)
  const sundayOf = (mondayIso) => {
    const [y, m, d] = mondayIso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + 6);
    return dt.toISOString().slice(0, 10);
  };
  const [hoursRange, setHoursRange] = useState({ startDate: mondayStr(), endDate: sundayOf(mondayStr()) });
  const [hoursSummary, setHoursSummary] = useState([]);

  useEffect(() => {
    loadStaff();
    loadTimeOffRequests();
    loadHoursSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadWeekShifts(weekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const loadWeekShifts = async (ws) => {
    try {
      const res = await api.get(`${API_BASE_URL}/api/staff/schedules/current-week/${user?.teamId}?week_start=${ws}`);
      setWeekShifts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error loading coverage:', error);
    }
  };

  const weekDates = () => {
    const [y, m, d] = weekStart.split('-').map(Number);
    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + i);
      return dt.toISOString().slice(0, 10);
    });
  };

  const toggleCoach = (id) => setBlockForm(f => ({
    ...f, coach_ids: f.coach_ids.includes(id) ? f.coach_ids.filter(c => c !== id) : [...f.coach_ids, id]
  }));
  const allCoachesSelected = staff.length > 0 && blockForm.coach_ids.length === staff.length;
  const toggleAllCoaches = () => setBlockForm(f => ({
    ...f, coach_ids: allCoachesSelected ? [] : staff.map(s => s.id)
  }));

  const handleBulkAssign = async () => {
    if (blockForm.coach_ids.length === 0) { toast.error('Pick at least one coach'); return; }
    if (!blockForm.start_time || !blockForm.end_time) { toast.error('Set a start and end time'); return; }
    setSavingBlock(true);
    try {
      const body = {
        coach_ids: blockForm.coach_ids,
        start_time: blockForm.start_time,
        end_time: blockForm.end_time
      };
      if (blockForm.mode === 'single') {
        body.date = blockForm.date;
      } else {
        body.weekday = blockForm.weekday;
        body.start_date = blockForm.date;
        body.occurrences = blockForm.occurrences;
      }
      const res = await api.post(`${API_BASE_URL}/api/staff/schedules/bulk-assign`, body);
      toast.success(`Added ${res.data.created} coverage block(s)`);
      setBlockForm(f => ({ ...f, coach_ids: [] }));
      loadWeekShifts(weekStart);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add hours');
    } finally {
      setSavingBlock(false);
    }
  };

  const shiftWeek = (deltaDays) => {
    const [y, m, d] = weekStart.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + deltaDays);
    setWeekStart(dt.toISOString().slice(0, 10));
  };

  const loadHoursSummary = async (range = hoursRange) => {
    try {
      const { startDate, endDate } = range;
      const res = await api.get(`${API_BASE_URL}/api/staff/hours-summary/${user?.teamId}?startDate=${startDate}&endDate=${endDate}`);
      setHoursSummary(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error loading hours summary:', error);
    }
  };

  const exportHoursCsv = () => {
    if (hoursSummary.length === 0) { toast.error('Nothing to export'); return; }
    const header = ['Name', 'Role', 'Shifts', 'Total Hours'];
    const lines = [header, ...hoursSummary.map(h => [h.name, h.role, h.shifts, h.total_hours])]
      .map(r => r.map(c => `"${String(c ?? '')}"`).join(',')).join('\n');
    const blob = new Blob([lines], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff-hours-${hoursRange.startDate}_to_${hoursRange.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalHours = hoursSummary.reduce((s, h) => s + Number(h.total_hours || 0), 0);

  const loadStaff = async () => {
    try {
      // Coaches AND admins are schedulable staff (an admin who also coaches
      // has role 'admin' — include them so they can be assigned shifts).
      const response = await api.get(`${API_BASE_URL}/api/admin/users?role=coach,admin&active_only=true`);
      setStaff(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const loadTimeOffRequests = async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/api/staff/time-off/team/${user?.teamId}?status=pending`);
      setTimeOffRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading time off requests:', error);
    }
  };

  const handleApproveTimeOff = async (requestId, status) => {
    try {
      await api.put(`${API_BASE_URL}/api/staff/time-off/${requestId}`, { status });
      toast.success(`Request ${status}`);
      loadTimeOffRequests();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Remove this shift?')) return;
    try {
      await api.delete(`${API_BASE_URL}/api/staff/schedules/shifts/${shiftId}`);
      loadWeekShifts(weekStart);
    } catch (error) {
      toast.error('Failed to remove shift');
    }
  };

  // --- Derived metrics for header tiles + grid ---
  const blocksForCoachDate = (coachId, dt) =>
    weekShifts.filter(s => s.user_id === coachId && String(s.shift_date).slice(0, 10) === dt);
  const coachWeekHours = (coachId) =>
    weekShifts.filter(s => s.user_id === coachId).reduce((sum, b) => sum + hoursBetween(b.start_time, b.end_time), 0);
  const weekTotalHours = weekShifts.reduce((sum, b) => sum + hoursBetween(b.start_time, b.end_time), 0);
  const coversToday = (() => {
    const today = new Date().toISOString().slice(0, 10);
    const ids = new Set(weekShifts.filter(s => String(s.shift_date).slice(0, 10) === today).map(s => s.user_id));
    return ids.size;
  })();

  return (
    <div className="ss-page">
      {!embedded && (
        <div className="ss-header">
          <div>
            <h1 className="ss-title">Staff Scheduling</h1>
            <p className="ss-subtitle">Set coverage, manage time off, and track hours</p>
          </div>
        </div>
      )}

      {/* Summary tiles */}
      <div className="ss-tiles">
        <div className="ss-tile">
          <span className="ss-tile-num">{staff.length}</span>
          <span className="ss-tile-label">Coaches</span>
        </div>
        <div className="ss-tile tile-blue">
          <span className="ss-tile-num">{weekTotalHours.toFixed(0)}</span>
          <span className="ss-tile-label">Coverage hrs this week</span>
        </div>
        <div className="ss-tile tile-green">
          <span className="ss-tile-num">{coversToday}</span>
          <span className="ss-tile-label">In the gym today</span>
        </div>
        <div className={`ss-tile ${timeOffRequests.length ? 'tile-orange' : ''}`}>
          <span className="ss-tile-num">{timeOffRequests.length}</span>
          <span className="ss-tile-label">Time-off pending</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="ss-tabs">
        <button className={`ss-tab ${selectedView === 'workhours' ? 'active' : ''}`} onClick={() => setSelectedView('workhours')}>
          🗓 Coverage
        </button>
        <button className={`ss-tab ${selectedView === 'timeoff' ? 'active' : ''}`} onClick={() => setSelectedView('timeoff')}>
          🌴 Time Off {timeOffRequests.length > 0 && <span className="ss-tab-badge">{timeOffRequests.length}</span>}
        </button>
        <button className={`ss-tab ${selectedView === 'hours' ? 'active' : ''}`} onClick={() => setSelectedView('hours')}>
          ⏱ Hours Summary
        </button>
      </div>

      {/* ===== Coverage ===== */}
      {selectedView === 'workhours' && (
        <>
          {/* Add coverage */}
          <div className="ss-card">
            <div className="ss-card-head">
              <h2>Add coverage</h2>
              <p>Pick a time, then choose who's in — one coach, several, or everyone.</p>
            </div>

            <div className="ss-add-grid">
              {/* Coaches */}
              <div className="ss-add-coaches">
                <div className="ss-add-label-row">
                  <span className="ss-add-label">Coaches</span>
                  <button className="ss-mini-btn" onClick={toggleAllCoaches}>
                    {allCoachesSelected ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <div className="ss-coach-chips">
                  {staff.map(m => {
                    const on = blockForm.coach_ids.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        className={`ss-coach-chip ${on ? 'on' : ''}`}
                        onClick={() => toggleCoach(m.id)}
                        style={on ? { borderColor: colorFor(m.id), background: `${colorFor(m.id)}18` } : {}}
                      >
                        <span className="ss-chip-avatar" style={{ background: colorFor(m.id) }}>{initials(m.name)}</span>
                        <span className="ss-chip-name">{m.name}</span>
                        {on && <span className="ss-chip-check" style={{ color: colorFor(m.id) }}>✓</span>}
                      </button>
                    );
                  })}
                  {staff.length === 0 && <span className="ss-muted">No coaches found.</span>}
                </div>
              </div>

              {/* Time + when */}
              <div className="ss-add-when">
                <div className="ss-field">
                  <label>Time</label>
                  <div className="ss-time-range">
                    <input type="time" value={blockForm.start_time} onChange={e => setBlockForm({ ...blockForm, start_time: e.target.value })} />
                    <span className="ss-dash">–</span>
                    <input type="time" value={blockForm.end_time} onChange={e => setBlockForm({ ...blockForm, end_time: e.target.value })} />
                  </div>
                </div>

                <div className="ss-field">
                  <label>When</label>
                  <div className="ss-seg">
                    <button className={`ss-seg-btn ${blockForm.mode === 'single' ? 'active' : ''}`} onClick={() => setBlockForm({ ...blockForm, mode: 'single' })}>One day</button>
                    <button className={`ss-seg-btn ${blockForm.mode === 'recurring' ? 'active' : ''}`} onClick={() => setBlockForm({ ...blockForm, mode: 'recurring' })}>Every week</button>
                  </div>

                  {blockForm.mode === 'single' ? (
                    <input className="ss-when-input" type="date" value={blockForm.date} onChange={e => setBlockForm({ ...blockForm, date: e.target.value })} />
                  ) : (
                    <div className="ss-recurring">
                      <select value={blockForm.weekday} onChange={e => setBlockForm({ ...blockForm, weekday: parseInt(e.target.value, 10) })}>
                        {WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>)}
                      </select>
                      <span>for</span>
                      <input type="number" min="1" max="52" value={blockForm.occurrences} onChange={e => setBlockForm({ ...blockForm, occurrences: parseInt(e.target.value, 10) || 1 })} />
                      <span>weeks from</span>
                      <input type="date" value={blockForm.date} onChange={e => setBlockForm({ ...blockForm, date: e.target.value })} />
                    </div>
                  )}
                </div>

                <button className="ss-primary-btn" onClick={handleBulkAssign} disabled={savingBlock}>
                  {savingBlock ? 'Adding…' : `Add coverage for ${blockForm.coach_ids.length || 'no'} coach${blockForm.coach_ids.length === 1 ? '' : 'es'}`}
                </button>
              </div>
            </div>
          </div>

          {/* Coverage grid */}
          <div className="ss-card">
            <div className="ss-week-head">
              <div>
                <h2>Who's in the gym</h2>
                <p className="ss-note">Assign what each coach runs (classes, groups, stations) on the calendar Practice Plan.</p>
              </div>
              <div className="ss-week-nav">
                <button className="ss-nav-btn" onClick={() => shiftWeek(-7)}>◀</button>
                <span className="ss-week-label">Week of {new Date(`${weekStart}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                <button className="ss-nav-btn" onClick={() => shiftWeek(7)}>▶</button>
              </div>
            </div>

            {staff.length === 0 ? (
              <p className="ss-empty">No coaches/admins found.</p>
            ) : (
              <div className="ss-grid-scroll">
                <table className="ss-grid">
                  <thead>
                    <tr>
                      <th className="ss-grid-corner">Coach</th>
                      {weekDates().map(dt => {
                        const isToday = dt === new Date().toISOString().slice(0, 10);
                        return (
                          <th key={dt} className={isToday ? 'ss-today-col' : ''}>
                            <span className="ss-day-name">{new Date(`${dt}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            <span className="ss-day-num">{new Date(`${dt}T00:00:00`).getDate()}</span>
                          </th>
                        );
                      })}
                      <th className="ss-hours-col">Hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(member => (
                      <tr key={member.id}>
                        <td className="ss-coach-cell">
                          <span className="ss-grid-avatar" style={{ background: colorFor(member.id) }}>{initials(member.name)}</span>
                          <span className="ss-grid-coach-info">
                            <strong>{member.name}</strong>
                            <small>{member.role}</small>
                          </span>
                        </td>
                        {weekDates().map(dt => {
                          const blocks = blocksForCoachDate(member.id, dt);
                          const isToday = dt === new Date().toISOString().slice(0, 10);
                          return (
                            <td key={dt} className={`ss-day-cell ${isToday ? 'ss-today-col' : ''}`}>
                              {blocks.length === 0 ? (
                                <span className="ss-cell-empty">–</span>
                              ) : blocks.map(b => (
                                <div key={b.id} className="ss-block" style={{ background: `${colorFor(member.id)}1a`, borderColor: colorFor(member.id) }}>
                                  <span className="ss-block-time">{hhmm(b.start_time)}–{hhmm(b.end_time)}</span>
                                  <button className="ss-block-x" title="Remove" onClick={() => handleDeleteShift(b.id)}>✕</button>
                                </div>
                              ))}
                            </td>
                          );
                        })}
                        <td className="ss-hours-cell">{coachWeekHours(member.id).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== Time Off ===== */}
      {selectedView === 'timeoff' && (
        <div className="ss-card">
          <div className="ss-card-head"><h2>Pending time-off requests</h2></div>
          {timeOffRequests.length === 0 ? (
            <p className="ss-empty">🌴 No pending time-off requests</p>
          ) : (
            <div className="ss-timeoff-list">
              {timeOffRequests.map(request => (
                <div key={request.id} className="ss-timeoff-card">
                  <div className="ss-to-left">
                    <span className="ss-grid-avatar" style={{ background: colorFor(request.staff_id || request.id) }}>{initials(request.staff_name)}</span>
                    <div>
                      <strong>{request.staff_name}</strong>
                      <div className="ss-to-meta">
                        <span className="ss-pill ss-pill-blue">{request.request_type}</span>
                        <span className="ss-to-dates">
                          {request.start_date === request.end_date
                            ? new Date(request.start_date).toLocaleDateString()
                            : `${new Date(request.start_date).toLocaleDateString()} – ${new Date(request.end_date).toLocaleDateString()}`}
                          {!request.is_all_day && request.start_time && <> · {hhmm(request.start_time)}–{hhmm(request.end_time)}</>}
                        </span>
                      </div>
                      {request.reason && <p className="ss-to-reason">"{request.reason}"</p>}
                    </div>
                  </div>
                  <div className="ss-to-actions">
                    <button className="ss-approve" onClick={() => handleApproveTimeOff(request.id, 'approved')}>✓ Approve</button>
                    <button className="ss-deny" onClick={() => handleApproveTimeOff(request.id, 'denied')}>✗ Deny</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== Hours Summary ===== */}
      {selectedView === 'hours' && (
        <div className="ss-card">
          <div className="ss-card-head">
            <h2>Hours summary</h2>
            <p>Actual clocked hours in the selected date range.</p>
          </div>

          <div className="ss-hours-filters">
            <div className="ss-field">
              <label>Start</label>
              <input type="date" value={hoursRange.startDate} onChange={(e) => setHoursRange({ ...hoursRange, startDate: e.target.value })} />
            </div>
            <div className="ss-field">
              <label>End</label>
              <input type="date" value={hoursRange.endDate} onChange={(e) => setHoursRange({ ...hoursRange, endDate: e.target.value })} />
            </div>
            <button className="ss-primary-btn ss-inline-btn" onClick={() => loadHoursSummary()}>Apply</button>
            <button className="ss-mini-btn" onClick={() => {
              const r = { startDate: mondayStr(), endDate: sundayOf(mondayStr()) };
              setHoursRange(r); loadHoursSummary(r);
            }}>This week</button>
          </div>

          {hoursSummary.length === 0 ? (
            <p className="ss-empty">No hours recorded in this range.</p>
          ) : (
            <>
              <table className="ss-hours-table">
                <thead>
                  <tr><th>Coach</th><th>Shifts</th><th>Total Hours</th></tr>
                </thead>
                <tbody>
                  {hoursSummary.map(h => (
                    <tr key={h.user_id}>
                      <td>
                        <span className="ss-grid-avatar sm" style={{ background: colorFor(h.user_id) }}>{initials(h.name)}</span>
                        <strong>{h.name}</strong> <small className="ss-muted">({h.role})</small>
                      </td>
                      <td>{h.shifts}</td>
                      <td><strong>{Number(h.total_hours).toFixed(2)}</strong> hrs</td>
                    </tr>
                  ))}
                  <tr className="ss-total-row">
                    <td><strong>Team total</strong></td>
                    <td />
                    <td><strong>{totalHours.toFixed(2)} hrs</strong></td>
                  </tr>
                </tbody>
              </table>
              <button className="ss-primary-btn" style={{ marginTop: 16 }} onClick={exportHoursCsv}>📥 Export CSV</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default StaffScheduling;
